import React, { useState } from 'react';
import { KeyRound, Loader2, LogIn } from 'lucide-react';
import { authService } from '../../services/authService';
import { userOrigin } from '../../config/api';
import LanguageSelector from '../../components/LanguageSelector';
import ImageCaptchaField from '../../components/ImageCaptchaField';
import SliderCaptcha from '../../components/SliderCaptcha';
import TurnstileWidget from '../../components/TurnstileWidget';
import { useTranslation } from '../../i18n/LocaleContext';

const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';

// 后台域名专用登录页：只有账号密码，没有社交登录和注册入口。
// 会员账号即使密码正确也会被挡在门外，不会被悄悄踢到别的域名。
const AdminLoginPage = ({ onSuccess, notice }) => {
  const { t, language } = useTranslation();
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaRefresh, setCaptchaRefresh] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaCode) {
      setError(t('imageCaptchaRequired'));
      setCaptchaRefresh((k) => k + 1);
      return;
    }
    if (TURNSTILE_SITEKEY ? !turnstileToken : !captchaOk) {
      setError(t('captchaRequired'));
      return;
    }
    setLoading(true);
    try {
      const response = await authService.adminLogin({ ...formData, captchaId, captchaCode, turnstileToken });
      onSuccess(response.user);
    } catch (err) {
      if (err.message === 'NO_ADMIN_PERMISSION') {
        setError(t('aAuthNoPermission'));
      } else {
        setError(err.message || t('loginFailed'));
      }
      setCaptchaRefresh((k) => k + 1);
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const info = error || notice;

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <div className="bg-brand-500">
        <div className="max-w-md mx-auto w-full px-5 pt-5 pb-24">
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-semibold text-white">{t('adminPanel')}</span>
            <LanguageSelector className="text-white" />
          </div>
          <h1 className="mt-16 pb-2 text-[32px] font-extrabold text-white leading-[1.15]">
            {t('aAuthTitle')}
          </h1>
        </div>
      </div>

      <main className="flex-1 -mt-12 bg-white rounded-t-2xl">
        <div className="max-w-md mx-auto w-full px-5 pt-8 pb-10">
          <p className="text-[13px] text-ink-secondary">{t('aAuthSubtitle')}</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {info && (
              <p
                className={`rounded-xl px-3.5 py-2.5 text-[12.5px] font-medium ${
                  error ? 'bg-red-500 text-white' : 'bg-yellow-400 text-gray-900'
                }`}
              >
                {info}
              </p>
            )}

            <label className="block">
              <span className="block mb-1.5 text-[12px] font-semibold text-ink-secondary">
                {t('aAuthIdentifier')}
              </span>
              <input
                type="text"
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                required
                autoComplete="username"
                className="w-full h-[50px] pl-4 pr-3.5 rounded-full bg-white border border-gray-200
                  text-[15px] text-ink placeholder:text-ink-muted/80 outline-none transition-colors
                  focus:border-brand-500"
              />
            </label>

            <label className="block">
              <span className="block mb-1.5 text-[12px] font-semibold text-ink-secondary">
                {t('aAuthPassword')}
              </span>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full h-[50px] pl-4 pr-3.5 rounded-full bg-white border border-gray-200
                  text-[15px] text-ink placeholder:text-ink-muted/80 outline-none transition-colors
                  focus:border-brand-500"
              />
            </label>

            <ImageCaptchaField
              refreshSignal={captchaRefresh}
              onChange={(code, id) => { setCaptchaCode(code); setCaptchaId(id); }}
            />

            <div className="flex justify-center pt-1">
              {TURNSTILE_SITEKEY ? (
                // key 带 language:头部切换语言时重挂挂件,让挂件文案同步
                <TurnstileWidget key={`${turnstileKey}-${language}`} onToken={setTurnstileToken} theme="light" />
              ) : (
                <SliderCaptcha onVerified={setCaptchaOk} />
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-[52px] mt-1 flex items-center justify-center gap-2 rounded-xl
                bg-brand-500 text-[15px] font-bold text-white hover:bg-brand-600
                disabled:opacity-60 transition-colors"
            >
              {loading
                ? <Loader2 className="w-[18px] h-[18px] animate-spin" />
                : <LogIn className="w-[18px] h-[18px]" strokeWidth={2.2} />}
              {loading ? t('aAuthSubmitting') : t('aAuthSubmit')}
            </button>
          </form>

          <a
            href={userOrigin()}
            className="mt-8 flex items-center justify-center gap-2 text-[13px] font-medium text-ink-secondary hover:text-ink transition-colors"
          >
            <KeyRound className="w-4 h-4" />
            {t('aAuthBackToSite')}
          </a>
        </div>
      </main>
    </div>
  );
};

export default AdminLoginPage;
